import { GROUPS_SORT_OPTIONS } from "@components/groups/groups-constants";
import { GROUPS_TABLE_COLUMN_LABELS } from "@components/table/groups/groups-table-constants";
import { GroupsTableColumnField } from "@components/table/groups/groups-table-types";
import { MEMBERS_SORT_OPTIONS } from "@components/table/members/members-sort-constants";
import { MEMBERS_TABLE_COLUMN_LABELS } from "@components/table/members/members-table-constants";
import { MembersSortField } from "@components/table/members/members-table-constants";
import { MembersTableColumnField } from "@components/table/members/members-table-types";
import { TEAMS_TABLE_COLUMN_LABELS } from "@components/table/teams/teams-table-constants";
import { TeamsTableColumnField } from "@components/table/teams/teams-table-types";
import { TEAMS_SORT_OPTIONS } from "@components/teams/teams-constants";
import {
  GROUPS_SORT_BY,
  GroupsSortBy,
  GroupsSortField,
  GroupTypes,
} from "@custom-types/group-types";
import { MEMBERS_SORT_BY, MembersSortBy } from "@custom-types/member-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import {
  TEAMS_SORT_BY,
  TeamsSortBy,
  TeamsSortField,
} from "@custom-types/teams-types";
import {
  SNAPSHOTS_SORT_OPTIONS,
  SNAPSHOTS_TABLE_COLUMN_LABELS,
} from "@pages/project-details/project-snapshots/snapshots-table-constants";
import {
  SNAPSHOTS_SORT_BY,
  SnapshotsSortBy,
  SnapshotsTableColumnField,
  SnapshotsSortField,
} from "@pages/project-details/project-snapshots/snapshots-table-types";
import { FaroCoreApiSchemas, FaroCoreApiTypes } from "@stellar/core-api-client";
import { ElementType } from "react";

const GROUPS_SORT_BY_SET = new Set<string>(GROUPS_SORT_BY);
const TEAMS_SORT_BY_SET = new Set<string>(TEAMS_SORT_BY);
const MEMBERS_SORT_BY_SET = new Set<string>(MEMBERS_SORT_BY);
const SNAPSHOTS_SORT_BY_SET = new Set<string>(SNAPSHOTS_SORT_BY);
const TEAM_MEMBERS_SORT_BY_SET = new Set<string>(
  FaroCoreApiSchemas.teamMemberSortBySchema.options
);

/** Determine whether the type is for details of a group */
export function isGroupDetails(
  group: GroupTypes
): group is FaroCoreApiTypes.GroupDetailResponse {
  // Use the unique fields "members";
  return "members" in group;
}

/** Whether the project status is of type ProjectArchivingState */
export function isProjectArchivingState(
  projectStatus: ProjectArchivingState | undefined
): projectStatus is ProjectArchivingState {
  return (
    projectStatus !== undefined &&
    Object.values(ProjectArchivingState).includes(projectStatus)
  );
}

/** Determine whether the provided image src is png or svg */
export function isSrcOfTypeString(src: string | ElementType): src is string {
  return typeof src === "string";
}

/** Whether the value is in type of string[] or not */
export function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    // value.every returns true for an empty array. So it needs to be checked separately
    value.length > 0 &&
    value.every((item) => typeof item === "string")
  );
}

/** @returns Whether the value is a FileSystemDirectoryHandle. */
export function isFileSystemDirectoryHandle(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemDirectoryHandle {
  return (
    ((value as FileSystemHandle).kind === "directory" || value.isDirectory) &&
    (value as FileSystemDirectoryHandle).entries instanceof Function
  );
}

/** @returns Whether the value is a FileSystemDirectoryEntry. */
export function isFileSystemDirectoryEntry(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemDirectoryEntry {
  return (
    value.isDirectory &&
    (value as FileSystemDirectoryEntry).createReader instanceof Function
  );
}

/** @returns Whether the value is a FileSystemFileHandle. */
export function isFileSystemFileHandle(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemFileHandle {
  return (
    ((value as FileSystemHandle).kind === "file" || value.isFile) &&
    (value as FileSystemFileHandle).getFile instanceof Function
  );
}

/** @returns Whether the value is a FileSystemFileEntry. */
export function isFileSystemFileEntry(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemFileEntry {
  return (
    value.isFile && (value as FileSystemFileEntry).file instanceof Function
  );
}

/**
 * Type guard to check if a string is a valid GroupsSortField
 */
export function isGroupsSortField(field: string): field is GroupsSortField {
  return field in GROUPS_SORT_OPTIONS;
}

/** Type guard to check if a string is a valid SnapshotsSortField */
export function isSnapshotsSortField(
  field: string
): field is SnapshotsSortField {
  return field in SNAPSHOTS_SORT_OPTIONS;
}

/** Type guard to check if a value is a valid SnapshotsSortBy */
export function isSnapshotsSortBy(
  value: string | null
): value is SnapshotsSortBy {
  if (!value) {
    return false;
  }

  return SNAPSHOTS_SORT_BY_SET.has(value);
}

/** Type guard to check if a string is a valid SnapshotsTableColumnField */
export function isSnapshotsTableColumnField(
  key: string
): key is SnapshotsTableColumnField {
  return key in SNAPSHOTS_TABLE_COLUMN_LABELS;
}

/**
 * Type guard to check if a value is a valid GroupsSortBy
 */
export function isGroupsSortBy(value: string | null): value is GroupsSortBy {
  if (!value) {
    return false;
  }

  return GROUPS_SORT_BY_SET.has(value);
}

/** Type guard to check if a string is a valid GroupsTableColumnField */
export function isGroupsTableColumnField(
  key: string
): key is GroupsTableColumnField {
  return key in GROUPS_TABLE_COLUMN_LABELS;
}

/**
 * Type guard to check if a string is a valid MembersTableColumnField value.
 * Used to safely filter and map column fields from user preferences.
 */
export function isMembersTableColumnField(
  field: string
): field is MembersTableColumnField {
  return Object.keys(MEMBERS_TABLE_COLUMN_LABELS).includes(field);
}

/** Type guard to check if a string is a valid TeamsTableColumnField */
export function isTeamsTableColumnField(
  key: string
): key is TeamsTableColumnField {
  return key in TEAMS_TABLE_COLUMN_LABELS;
}

/**
 * Type guard to check if a value is a valid TeamsSortBy
 */
export function isTeamsSortBy(value: string | null): value is TeamsSortBy {
  if (!value) {
    return false;
  }
  return TEAMS_SORT_BY_SET.has(value);
}

/**
 * Type guard to check if a string is a valid TeamsSortField
 */
export function isTeamsSortField(field: string): field is TeamsSortField {
  return field in TEAMS_SORT_OPTIONS;
}

/**
 * Type guard to check if a value is a valid MembersSortBy
 */
export function isMembersSortBy(value: string | null): value is MembersSortBy {
  if (!value) {
    return false;
  }
  return MEMBERS_SORT_BY_SET.has(value);
}

/**
 * Type guard to check if a string is a valid MembersSortField
 */
export function isMembersSortField(field: string): field is MembersSortField {
  return field in MEMBERS_SORT_OPTIONS;
}

/**
 * Type guard to check if a value is a valid TeamMemberSortBy
 */
export function isTeamMembersSortField(
  value: string | null
): value is FaroCoreApiTypes.TeamMemberSortBy {
  if (!value) {
    return false;
  }
  return TEAM_MEMBERS_SORT_BY_SET.has(value);
}
